import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Phone, CheckCircle, Zap, Mountain, Star, Quote } from 'lucide-react';
import { companyInfo, cordisteServices, electricienServices, heroImages, testimonials } from '../data/mock';
import { getStructuredData } from '../utils/structuredData';
import { FAQ } from '../components/FAQ';
import { ZoneIntervention } from '../components/ZoneIntervention';

export const Home = () => {
  const structuredData = getStructuredData();
  return (
    <div className="min-h-screen bg-slate-900">
      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData.organization)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(structuredData.electricianService)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(structuredData.ropeAccessService)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(structuredData.reviews)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(structuredData.faq)}
      </script>
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-32 pb-20">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <img
            src={heroImages.main}
            alt="Alpielec"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/90 to-slate-900/70" />
        </div>

        {/* Content */}
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/20 backdrop-blur-sm border border-blue-400/30 rounded-full mb-6 animate-in fade-in slide-in-from-bottom-4 duration-700">
              <span className="w-2 h-2 bg-blue-400 rounded-full animate-pulse" />
              <span className="text-blue-300 text-sm font-medium">Expert Pays Basque</span>
            </div>

            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 animate-in fade-in slide-in-from-bottom-6 duration-700 delay-100">
              Deux Métiers, Un Professionnel
            </h1>
            
            <p className="text-xl md:text-2xl text-slate-300 mb-4 animate-in fade-in slide-in-from-bottom-8 duration-700 delay-200">
              <span className="text-blue-400 font-semibold">Électricien qualifié</span> ET <span className="text-blue-400 font-semibold">Cordiste professionnel</span> au Pays Basque.
            </p>
            
            <p className="text-lg text-slate-400 mb-4 animate-in fade-in slide-in-from-bottom-8 duration-700 delay-200">
              Deux activités distinctes et complètes que j'exerce <span className="text-white font-medium">séparément ou ensemble</span> selon vos besoins. Besoin uniquement d'un électricien ? D'un cordiste ? Ou des deux compétences pour un projet complexe ? Je m'adapte à votre demande.
            </p>
            
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-blue-500/10 backdrop-blur-sm border border-blue-400/30 rounded-xl mb-8 animate-in fade-in slide-in-from-bottom-8 duration-700 delay-250">
              <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0" />
              <span className="text-white font-medium">Interventions Particuliers & Professionnels</span>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 mb-12 animate-in fade-in slide-in-from-bottom-10 duration-700 delay-300">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-semibold transition-all duration-300 hover:shadow-xl hover:shadow-blue-500/30 hover:-translate-y-1 group"
              >
                Devis Gratuit
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <a
                href={`tel:${companyInfo.phone}`}
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-slate-800/80 backdrop-blur-sm hover:bg-slate-700 text-white border border-slate-700 rounded-xl font-semibold transition-all duration-300 hover:shadow-lg"
              >
                <Phone className="w-5 h-5" />
                {companyInfo.phone}
              </a>
            </div>

            <div className="flex flex-wrap gap-8 animate-in fade-in slide-in-from-bottom-12 duration-700 delay-500">
              <div className="flex items-center gap-2 text-slate-300">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span className="text-sm">Intervention rapide</span>
              </div>
              <div className="flex items-center gap-2 text-slate-300">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span className="text-sm">Devis gratuit</span>
              </div>
              <div className="flex items-center gap-2 text-slate-300">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span className="text-sm">Disponible 7j/7</span>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-slate-400 rounded-full flex items-start justify-center p-2">
            <div className="w-1.5 h-3 bg-slate-400 rounded-full" />
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20 bg-slate-950">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Deux Métiers Distincts
            </h2>
            <p className="text-xl text-slate-400 max-w-3xl mx-auto mb-6">
              Choisissez le service dont vous avez besoin : <span className="text-white font-medium">électricité seule</span>, <span className="text-white font-medium">cordiste seul</span>, ou <span className="text-blue-400 font-semibold">les deux combinés</span> pour vos projets nécessitant travaux électriques en hauteur
            </p>
            <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-slate-800/80 backdrop-blur-sm border border-slate-600 rounded-lg">
              <span className="text-slate-300 text-sm">✓ Particuliers</span>
              <span className="text-slate-600">•</span>
              <span className="text-slate-300 text-sm">✓ Professionnels</span>
              <span className="text-slate-600">•</span>
              <span className="text-slate-300 text-sm">✓ Copropriétés</span>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {/* Électricien Card */}
            <Link
              to="/electricien"
              className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-800 to-slate-900 border border-slate-700 hover:border-blue-500/50 transition-all duration-500 hover:shadow-2xl hover:shadow-blue-500/20 hover:-translate-y-2"
            >
              <div className="absolute inset-0">
                <img
                  src={heroImages.electricien}
                  alt="Services Électricien"
                  className="w-full h-full object-cover opacity-20 group-hover:opacity-30 transition-opacity duration-500 group-hover:scale-110 transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/80 to-transparent" />
              </div>

              <div className="relative z-10 p-8">
                <div className="w-16 h-16 bg-blue-500/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mb-6 group-hover:bg-blue-500/30 transition-colors">
                  <Zap className="w-8 h-8 text-blue-400" />
                </div>

                <h3 className="text-3xl font-bold text-white mb-4 group-hover:text-blue-400 transition-colors">
                  Électricien
                </h3>
                
                <p className="text-slate-300 mb-6">
                  Service complet d'électricité, <span className="text-white font-medium">indépendamment des travaux en hauteur</span>. Tous types d'installations électriques.
                </p>

                <ul className="space-y-3 mb-6">
                  <li className="flex items-center gap-2 text-slate-400">
                    <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0" />
                    Remises en Conformité
                  </li>
                  <li className="flex items-center gap-2 text-slate-400">
                    <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0" />
                    Dépannages Rapides
                  </li>
                  <li className="flex items-center gap-2 text-slate-400">
                    <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0" />
                    Rénovations Complètes
                  </li>
                  <li className="flex items-center gap-2 text-slate-400">
                    <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0" />
                    Travaux Neufs
                  </li>
                </ul>

                <div className="flex items-center gap-2 text-blue-400 font-semibold group-hover:gap-4 transition-all">
                  Découvrir les services
                  <ArrowRight className="w-5 h-5" />
                </div>
              </div>
            </Link>

            {/* Cordiste Card */}
            <Link
              to="/cordiste"
              className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-800 to-slate-900 border border-slate-700 hover:border-blue-500/50 transition-all duration-500 hover:shadow-2xl hover:shadow-blue-500/20 hover:-translate-y-2"
            >
              <div className="absolute inset-0">
                <img
                  src={heroImages.cordiste}
                  alt="Services Cordiste"
                  className="w-full h-full object-cover opacity-20 group-hover:opacity-30 transition-opacity duration-500 group-hover:scale-110 transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/80 to-transparent" />
              </div>

              <div className="relative z-10 p-8">
                <div className="w-16 h-16 bg-blue-500/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mb-6 group-hover:bg-blue-500/30 transition-colors">
                  <Mountain className="w-8 h-8 text-blue-400" />
                </div>

                <h3 className="text-3xl font-bold text-white mb-4 group-hover:text-blue-400 transition-colors">
                  Cordiste
                </h3>
                
                <p className="text-slate-300 mb-6">
                  Service complet de travaux en hauteur, <span className="text-white font-medium">indépendamment de l'électricité</span>. Accès par corde pour tous types de travaux.
                </p>

                <ul className="space-y-3 mb-6">
                  <li className="flex items-center gap-2 text-slate-400">
                    <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0" />
                    Travaux Haute Tension
                  </li>
                  <li className="flex items-center gap-2 text-slate-400">
                    <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0" />
                    Reprise de Maçonnerie
                  </li>
                  <li className="flex items-center gap-2 text-slate-400">
                    <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0" />
                    Port de Charge
                  </li>
                  <li className="flex items-center gap-2 text-slate-400">
                    <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0" />
                    Petits Travaux en Hauteur
                  </li>
                </ul>

                <div className="flex items-center gap-2 text-blue-400 font-semibold group-hover:gap-4 transition-all">
                  Découvrir les services
                  <ArrowRight className="w-5 h-5" />
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-slate-900">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Pourquoi Choisir Alpielec ?
            </h2>
            <p className="text-xl text-slate-400 max-w-2xl mx-auto">
              Une expertise reconnue au Pays Basque
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-2xl p-8 hover:border-blue-500/50 transition-all duration-300 hover:-translate-y-2">
              <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center mb-4">
                <CheckCircle className="w-6 h-6 text-blue-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Deux Métiers Complets</h3>
              <p className="text-slate-400">
                Électricien qualifié ET cordiste professionnel. Chaque métier exercé séparément ou combiné selon vos besoins spécifiques.
              </p>
            </div>

            <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-2xl p-8 hover:border-blue-500/50 transition-all duration-300 hover:-translate-y-2">
              <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center mb-4">
                <Zap className="w-6 h-6 text-blue-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Un Seul Interlocuteur</h3>
              <p className="text-slate-400">
                Plus besoin de coordonner deux entreprises différentes. Un seul professionnel pour vos besoins en électricité, en hauteur, ou les deux.
              </p>
            </div>

            <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-2xl p-8 hover:border-blue-500/50 transition-all duration-300 hover:-translate-y-2">
              <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center mb-4">
                <Mountain className="w-6 h-6 text-blue-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Flexibilité Totale</h3>
              <p className="text-slate-400">
                Besoin uniquement d'électricité ? Uniquement de cordiste ? Ou d'une combinaison des deux ? Je m'adapte à votre projet.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-slate-900">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/20 backdrop-blur-sm border border-blue-400/30 rounded-full mb-4">
              <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
              <span className="text-blue-300 text-sm font-medium">5/5 sur Google</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Ils nous font confiance
            </h2>
            <p className="text-xl text-slate-400 max-w-2xl mx-auto">
              Découvrez les avis de nos clients satisfaits
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
            {testimonials.slice(0, 3).map((testimonial) => (
              <div
                key={testimonial.id}
                className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-2xl p-8 hover:border-blue-500/50 transition-all duration-300 hover:-translate-y-2 relative"
              >
                <Quote className="absolute top-6 right-6 w-12 h-12 text-blue-500/10" />
                
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>

                <p className="text-slate-300 mb-6 leading-relaxed">
                  "{testimonial.text}"
                </p>

                <div className="flex items-center justify-between pt-4 border-t border-slate-700">
                  <div>
                    <p className="text-white font-semibold">{testimonial.name}</p>
                    <p className="text-sm text-slate-400">Avis {testimonial.source}</p>
                  </div>
                  <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                    <span className="text-blue-400 font-bold text-lg">
                      {testimonial.name.charAt(0)}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* View More Reviews Link */}
          <div className="text-center mt-12">
            <a
              href="https://www.google.com/search?q=alpielec"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 font-semibold transition-colors"
            >
              Voir tous nos avis Google
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </div>
      </section>

      {/* Zone d'intervention */}
      <ZoneIntervention />

      {/* FAQ Section */}
      <FAQ />

      {/* CTA Section */}
      <section className="py-20 bg-slate-950">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-gradient-to-br from-blue-600 to-blue-800 rounded-3xl p-12 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-blue-400/20 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-900/30 rounded-full blur-3xl" />
            
            <div className="relative z-10 text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Besoin d'un Devis ?
              </h2>
              <p className="text-xl text-blue-100 mb-8">
                Tous nos devis sont <span className="font-bold">gratuits</span> et sans engagement. Contactez-nous dès maintenant !
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white hover:bg-slate-100 text-blue-600 rounded-xl font-semibold transition-all duration-300 hover:shadow-xl hover:-translate-y-1 group"
                >
                  Demander un devis
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <a
                  href={`tel:${companyInfo.phone}`}
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-blue-700 hover:bg-blue-600 text-white border-2 border-blue-500 rounded-xl font-semibold transition-all duration-300"
                >
                  <Phone className="w-5 h-5" />
                  Appeler maintenant
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
